import { LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_DATE_FORMAT, SHORT_MONTH_DAY_YEAR_FORMAT, TODAY_IN_DAYS } from 'constants/date';
import { DataType } from 'constants/taskCategories';
import { AUTHORIZED_SIGNER_TASK_TYPE_ID, TaskAssignmentType, TaskStatus } from 'constants/tasks';
import { jsonParse } from 'utils/json';

import { SignerStepKey } from './components/TaskStepCompleteForm/TaskStepCompleteForm.constants';
import {
  AUTHORIZED_SIGNER_ASSIGNEE_LABEL,
  COMPANY_ADMIN_ASSIGNEE_LABEL,
  CURRENT_USER_ASSIGNEE_SUFFIX,
  DEFAULT_QUESTIONNAIRE_STEP_TITLE,
  QUESTIONNAIRE_TASK_TITLE_PREFIX,
  SignerStepTaskName,
  SIGNING_TASK_DISPLAY_NAME,
  TASK_DUE_SOON_DAYS,
  TASK_OVERDUE_DAYS,
  TASK_REFERENCE_ID_RADIX,
  TASK_REFERENCE_PREFIX,
  TASK_RELATIVE_DAYS_THRESHOLD,
  TASK_SIGNATURE_REQUIRED_DETAIL,
  TASK_ACTION_SIGN_LABEL,
  TASK_ACTION_SUBMIT_LABEL,
  TASK_ACTION_VIEW_LABEL,
  TaskDueTone,
  TaskFilterKey,
  TaskSubmitKind,
} from './TasksTab.constants';

const COMPANY_SCOPED_ASSIGNMENT_TYPES = [
  TaskAssignmentType.SUB_COMPANY,
  TaskAssignmentType.SUB_COMPANY_USER,
];

const DUE_TONE_LABEL_VARIANT = {
  [TaskDueTone.OVERDUE]: LabelVariant.RED,
  [TaskDueTone.DUE_SOON]: LabelVariant.ORANGE,
  [TaskDueTone.NEUTRAL]: LabelVariant.GRAY,
};

const STATUS_LABEL_VARIANT = {
  [TaskStatus.NEW]: LabelVariant.ORANGE,
  [TaskStatus.SUBMITTED]: LabelVariant.BLUE,
  [TaskStatus.APPROVED]: LabelVariant.GREEN,
  [TaskStatus.REJECTED]: LabelVariant.RED,
  [TaskStatus.RETURNED]: LabelVariant.PURPLE,
};

const getTaskAssignmentType = task => task.targetType ?? task.assignmentType ?? null;

const getTaskDueDate = ({ dueDate, documentRequestDeadline }) =>
  dueDate ?? documentRequestDeadline ?? null;

const getIsInternalTask = task =>
  task.taskType?.taskAssignmentType === TaskAssignmentType.SLA_GROUP &&
  getTaskAssignmentType(task) === TaskAssignmentType.SUB_COMPANY;

const getIsReturnedInternalTask = task =>
  task.status === TaskStatus.RETURNED && getIsInternalTask(task);

const BROKER_VISIBLE_INTERNAL_STATUSES = [
  TaskStatus.RETURNED,
  TaskStatus.SUBMITTED,
  TaskStatus.APPROVED,
];

// The backend only sends an internal task to the broker once the lender has returned it, so the
// broker keeps seeing it after resubmitting (Submitted) and once the lender approves the answer
// (Completed), and can track what was sent and how it ended.
export const getCompanyScopedTasks = tasks =>
  tasks.filter(task =>
    getIsInternalTask(task)
      ? BROKER_VISIBLE_INTERNAL_STATUSES.includes(task.status)
      : COMPANY_SCOPED_ASSIGNMENT_TYPES.includes(getTaskAssignmentType(task)),
  );

export const getTaskStatus = task => task.status ?? TaskStatus.NEW;

const getTaskFilterKey = task => {
  const status = getTaskStatus(task);

  return status === TaskStatus.RETURNED ? TaskFilterKey.NEW : status;
};

export const getTasksByFilter = (tasks, filterKey) =>
  tasks.filter(task => getTaskFilterKey(task) === filterKey);

export const getTaskCountsByFilter = tasks =>
  Object.values(TaskFilterKey).reduce((counts, filterKey) => {
    counts[filterKey] = getTasksByFilter(tasks, filterKey).length;

    return counts;
  }, {});

export const getTaskDueTone = task => {
  const dueDate = getTaskDueDate(task);

  if (!dueDate) {
    return TaskDueTone.NEUTRAL;
  }

  const daysUntilDue = dayjs(dueDate).startOf('day').diff(TODAY_IN_DAYS, 'day');

  if (daysUntilDue < TASK_DUE_SOON_DAYS) {
    return TaskDueTone.OVERDUE;
  }

  if (daysUntilDue < TASK_OVERDUE_DAYS) {
    return TaskDueTone.DUE_SOON;
  }

  return TaskDueTone.NEUTRAL;
};

export const getTaskDueLabelVariant = tone => DUE_TONE_LABEL_VARIANT[tone];

export const getTaskStatusLabelVariant = status =>
  STATUS_LABEL_VARIANT[status] ?? LabelVariant.GRAY;

export const getTaskDueLabel = task => {
  const dueDate = getTaskDueDate(task);

  if (!dueDate) {
    return null;
  }

  const daysUntilDue = dayjs(dueDate).startOf('day').diff(TODAY_IN_DAYS, 'day');
  const formattedDate = dayjs(dueDate).format(COMMON_DATE_FORMAT);

  if (daysUntilDue < 0) {
    return `${formattedDate} · overdue`;
  }

  return `${formattedDate} · ${daysUntilDue}d`;
};

export const getTaskLastModifiedLabel = task => {
  if (!task.lastModified) {
    return null;
  }

  const daysAgo = TODAY_IN_DAYS.diff(dayjs(task.lastModified).startOf('day'), 'day');

  if (daysAgo < 0 || daysAgo > TASK_RELATIVE_DAYS_THRESHOLD) {
    return dayjs(task.lastModified).format(SHORT_MONTH_DAY_YEAR_FORMAT);
  }

  if (daysAgo === 0) {
    return 'Today';
  }

  if (daysAgo === 1) {
    return '1 day ago';
  }

  return `${daysAgo} days ago`;
};

export const getTaskLastModifiedTooltip = task =>
  task.lastModified ? dayjs(task.lastModified).format(SHORT_MONTH_DAY_YEAR_FORMAT) : null;

export const getTaskAssigneeLabel = (task, currentUserId) => {
  // Authorized-signer tasks (the signing task + the deferred step-completion tasks) are
  // scoped to the signer, not the whole company — surface the role (and the chosen signer's
  // name when known) rather than the SUB_COMPANY default "Company admin".
  if (getIsSignerTask(task)) {
    const signerName = task.assignedUserName || task.assignedUserEmail;

    return signerName
      ? `${AUTHORIZED_SIGNER_ASSIGNEE_LABEL} · ${signerName}`
      : AUTHORIZED_SIGNER_ASSIGNEE_LABEL;
  }

  if (getTaskAssignmentType(task) === TaskAssignmentType.SUB_COMPANY) {
    return COMPANY_ADMIN_ASSIGNEE_LABEL;
  }

  const name = task.assignedUserName || task.assignedUserEmail || COMPANY_ADMIN_ASSIGNEE_LABEL;

  if (task.assignedUserId && task.assignedUserId === currentUserId) {
    return `${name} · ${CURRENT_USER_ASSIGNEE_SUFFIX}`;
  }

  return name;
};

export const getIsCompanyWideTask = task =>
  !getIsSignerTask(task) && getTaskAssignmentType(task) === TaskAssignmentType.SUB_COMPANY;

export const getIsSigningTask = task =>
  !!task.isSignatureRequired || task.taskTypeId === AUTHORIZED_SIGNER_TASK_TYPE_ID;

// metadata arrives as a JSON string; null-guard so a malformed blob degrades to no
// deep-link rather than throwing and blanking the drawer.
const getTaskMetadata = task => {
  if (_.isNil(task) || _.isNil(task.metadata)) {
    return null;
  }

  try {
    return jsonParse(task.metadata) ?? null;
  } catch {
    return null;
  }
};

export const getTaskCertificationId = task => getTaskMetadata(task)?.certificationId ?? null;

// The deferred Attestation / Compensation-Plans signer tasks carry the wizard step they defer
// (metadata.stepKey) so the drawer can pick the matching step-completion surface.
export const getTaskStepKey = task => getTaskMetadata(task)?.stepKey ?? null;

// A Questionnaire signer task is one of several (one per visible questionnaire instance), so it
// carries the instance it reviews (metadata.stepInstanceId) and that instance's title.
export const getTaskStepInstanceId = task => getTaskMetadata(task)?.stepInstanceId ?? null;

const getTaskStepTitle = task => {
  const stepTitle = getTaskMetadata(task)?.stepTitle;

  return _.isString(stepTitle) && stepTitle.trim().length > 0 ? stepTitle.trim() : null;
};

// Signer-only tasks = the signing task OR a deferred step-completion task (carries metadata.stepKey).
// These are assigned to the authorized signer, not the whole sub-company.
export const getIsSignerTask = task => getIsSigningTask(task) || !_.isNil(getTaskStepKey(task));

const getQuestionnaireTaskTitle = stepTitle => `${QUESTIONNAIRE_TASK_TITLE_PREFIX} — ${stepTitle}`;

// Signer tasks share a generic TaskType name; render a clearer step- or action-specific name in
// the Task column: a step-completion task (metadata.stepKey) maps to its step name, and the signing
// task (no stepKey) reads as "Sign Documents". Falls back to task.name for every other task.
export const getTaskDisplayName = task => {
  const stepKey = getTaskStepKey(task);
  const stepName = SignerStepTaskName[stepKey];
  const stepTitle = getTaskStepTitle(task);

  const isCustomQuestionnaireTitle =
    !_.isNil(stepTitle) &&
    stepTitle.toLowerCase() !== DEFAULT_QUESTIONNAIRE_STEP_TITLE.toLowerCase();

  if (stepName && isCustomQuestionnaireTitle && stepKey === SignerStepKey.QUESTIONNAIRE) {
    return getQuestionnaireTaskTitle(stepTitle);
  }

  if (stepName) {
    return stepName;
  }

  if (getIsSigningTask(task)) {
    return SIGNING_TASK_DISPLAY_NAME;
  }

  return task.name;
};

// Routing precedence: signing identity first, then a step-completion task (carries metadata.stepKey
// but is NOT a signing task — isSignatureRequired stays false so it does not misroute to the signing
// surface), then the task type's data kind.
//
// The document-vs-acknowledgment split must follow the task type's DataType, because that is what
// Skynet.Loans TaskModel.ThrowIfSubmitIsNotValid validates the submit against: a Documents type
// demands a file, and every other type rejects one (STP-678).
//
// A returned internal task is answered with a document and/or a note whatever its type's data kind,
// because the backend relaxes the submit rules for internal returns only.
//
// Every other data kind (Payment, Forms, IncomeVerification, ...) is UNSUPPORTED rather than
// acknowledgment: the acknowledgment form posts a fixed attestation, and the backend accepts a
// text-only submit for those kinds, so falling through would offer one click that marks a payment or
// a verification as done under a statement the user never wrote. This keeps the drawer from offering
// that path — the contract itself is only enforceable server-side (STP-697). Only the SkynetAPI list
// carries the nested taskType, so an absent dataType keeps the acknowledgment form the FreeText types
// that make up nearly every sub-company task need.
export const getTaskSubmitKind = task => {
  if (getIsSigningTask(task)) {
    return TaskSubmitKind.SIGNING;
  }

  if (!_.isNil(getTaskStepKey(task))) {
    return TaskSubmitKind.STEP_COMPLETE;
  }

  if (getIsReturnedInternalTask(task)) {
    return TaskSubmitKind.RETURNED;
  }

  const dataType = task.taskType?.dataType;

  if (dataType === DataType.DOCUMENTS) {
    return TaskSubmitKind.DOCUMENT;
  }

  return _.isNil(dataType) || dataType === DataType.FREE_TEXT
    ? TaskSubmitKind.ACKNOWLEDGMENT
    : TaskSubmitKind.UNSUPPORTED;
};

export const getTaskReferenceId = () =>
  `${TASK_REFERENCE_PREFIX}${_.now().toString(TASK_REFERENCE_ID_RADIX).toUpperCase()}`;

export const getTaskDetailText = task => {
  const status = getTaskStatus(task);

  if (status === TaskStatus.REJECTED && task.rejectionReason) {
    return `Rejected: ${task.rejectionReason}`;
  }

  // A NEW signing task must read as a signature request, not a generic acknowledgment —
  // the config-driven description (e.g. "LO compensation rules") would otherwise mislead.
  if (status === TaskStatus.NEW && getIsSigningTask(task)) {
    return TASK_SIGNATURE_REQUIRED_DETAIL;
  }

  return task.description || null;
};

export const getTaskActionLabel = task => {
  const submitKind = getTaskSubmitKind(task);

  if (submitKind === TaskSubmitKind.SIGNING) {
    return TASK_ACTION_SIGN_LABEL;
  }

  const isViewOnly =
    submitKind === TaskSubmitKind.UNSUPPORTED && task.status === TaskStatus.RETURNED;

  return isViewOnly ? TASK_ACTION_VIEW_LABEL : TASK_ACTION_SUBMIT_LABEL;
};
